import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { ContractSigningTypes } from 'models/enums/ContractSigningTypes';
import ProductType from 'models/enums/ProductType';
import Insurer from 'models/productManagement/insurer/Model.Insurer';
import PricingDistributionTemplate from 'models/productManagement/pricingDistributionTemplate/Model.PricingDistributionTemplate';
import ProductCategory from 'models/productManagement/productCategory/Model.ProductCategory';
import Provider from 'models/productManagement/provider/Model.Provider';
import Subfee from 'models/productManagement/subfee/Model.Subfee';
import React from 'react';
import { useProductProvider } from '../../Product.Hooks';

const sectionStyle: React.CSSProperties = {
  borderTop: `1px solid var(--colorInputBorder)`,
  marginTop: Theme.Size.S,
  paddingBottom: Theme.Size.S,
  paddingTop: Theme.Size.S,
};
const switchGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: Theme.Size.XS,
};

export default function ProductEditInfo() {
  const { form } = useProductProvider();
  const { lang } = getLocales();
  const { payload: categoriesPayload } = useApi({ action: ProductCategory.list });
  const { payload: insurersPayload } = useApi({ action: Insurer.list });
  const { payload: subfeesPayload } = useApi({ action: Subfee.list });
  const { payload: distributionsPayload } = useApi({ action: PricingDistributionTemplate.list });
  const { payload: providersPayload } = useApi({ action: Provider.list });

  return (
    <FormWrapper class={ThemeWrapperPaper}>
      <div style={{ fontSize: Theme.FontSize.L, fontWeight: 'bold' }}>{lang.generalInformation}</div>
      <br />
      <TextInput
        name="name"
        label={lang.name}
        value={form.data.name}
        error={form.errors.name}
        onChange={form.update}
        icon={<Icon class="if-quill-pen" />}
      />
      <TextInput
        name="code"
        label={lang.code}
        value={form.data.code}
        error={form.errors.code}
        onChange={form.update}
        icon={<Icon class="if-quill-pen" />}
      />
      <SelectInput
        name="providerId"
        label={lang.provider}
        data={providersPayload?.data?.map((item) => ({
          ...item.displayInfo,
          icon: <Icon class="fas-building" />,
        }))}
        value={form.data.providerId}
        error={form.errors.providerId}
        onChange={form.update}
        icon={<Icon class="fas-building" />}
      />
      <SelectInput
        name="productType"
        label={lang.type}
        data={Object.values(ProductType).map((id) => ({
          id,
          title: lang[id] || id,
        }))}
        value={form.data.productType}
        error={form.errors.productType}
        onChange={({ productType }) => form.update({ productType })}
        icon={<Icon class="if-cloud" />}
      />
      <SelectInput
        name="category"
        label={lang.program}
        data={categoriesPayload?.data?.map((item) => ({
          ...item.displayInfo,
          icon: <Icon class="fas-box" />,
        }))}
        value={form.data.category?.id}
        error={form.errors.category?._objectError}
        onChange={({ category: id }) => form.update({ category: { id }, categoryId: id })}
        icon={<Icon class="if-cloud" />}
      />
      <TextInput
        name="limitOfLiability"
        label={lang.limitOfLiabilityAggregate}
        value={form.data.limitOfLiability}
        error={form.errors.limitOfLiability}
        onChange={form.update}
        icon={<Icon class="if-quill-pen" />}
      />
      <NumberInput
        name="cancellationFullRefundPeriodInDays"
        label={lang.cancellationFullRefundPeriodInDays}
        value={form.data.cancellationFullRefundPeriodInDays}
        error={form.errors.cancellationFullRefundPeriodInDays}
        onChange={form.update}
        icon={<Icon class="if-quill-pen" />}
      />
      <NumberInput
        name="maximumMarkup"
        label={lang.maximumMarkup}
        value={form.data.maximumMarkup}
        error={form.errors.maximumMarkup}
        onChange={form.update}
        icon={<Icon class="if-multiply" />}
      />
      <NumberInput
        name="maxVehicleAgeInYears"
        label={lang.maxVehicleAge}
        value={form.data.maxVehicleAgeInYears}
        error={form.errors.maxVehicleAgeInYears}
        onChange={form.update}
        icon={<Icon class="if-multiply" />}
      />
      <NumberInput
        name="maxFinanceToPurchasePriceRatio"
        label={lang.maxFinancedAmountRation}
        value={form.data.maxFinanceToPurchasePriceRatio}
        error={form.errors.maxFinanceToPurchasePriceRatio}
        onChange={form.update}
        icon={<Icon class="if-multiply" />}
      />
      <CheckInput
        name="isActive"
        label={lang.isActive}
        class={inputStyles.check.switch}
        style={{ margin: `${Theme.Size.S} 0` }}
        value={form.data.isActive}
        onChange={form.update}
        htmlElementProps={{ title: form.data.isActive ? lang.disable : lang.enable }}
      />
      <CheckInput
        name="isStoredInBMS"
        label={lang.storedInBMS}
        class={inputStyles.check.switch}
        style={{ margin: `${Theme.Size.S} 0` }}
        value={form.data.isStoredInBMS}
        onChange={form.update}
        htmlElementProps={{ title: form.data.isStoredInBMS ? lang.disable : lang.enable }}
      />
      <div
        style={{
          borderBottom: `1px solid var(--colorInputBorder)`,
          paddingBottom: Theme.Size.S,
          margin: `${Theme.Size.M} 0`,
        }}
      >
        <div style={sectionStyle}>
          <div style={switchGroupStyle}>
            <h4>{lang.contractOptions}</h4>
            <CheckInput
              name="canSellPostSale"
              label={lang.canSellPostSale}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.canSellPostSale}
              onChange={form.update}
              htmlElementProps={{ title: form.data.canSellPostSale ? lang.disable : lang.enable }}
            />
            <CheckInput
              name="isWarrantyDetailsVisible"
              label={lang.isWarrantyDetailsVisible}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isWarrantyDetailsVisible}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isWarrantyDetailsVisible ? lang.disable : lang.enable }}
            />
            <CheckInput
              name="isPurchaseDetailsVisible"
              label={lang.isPurchaseDetailsVisible}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isPurchaseDetailsVisible}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isPurchaseDetailsVisible ? lang.disable : lang.enable }}
            />
            <CheckInput
              name="isPartOfPackage"
              label={lang.isPartOfPackage}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isPartOfPackage}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isPartOfPackage ? lang.disable : lang.enable }}
            />
            <CheckInput
              name="isMappingProductForLegacyContracts"
              label={lang.isMappingProductForLegacyContracts}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isMappingProductForLegacyContracts}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isMappingProductForLegacyContracts ? lang.disable : lang.enable }}
            />
          </div>
        </div>
        <div style={sectionStyle}>
          <div style={switchGroupStyle}>
            <h4>{lang.documentSigning}</h4>
            <CheckInput
              name="isSendDocumentToSigningEnabled"
              label={lang.sendDocumentForSigning}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isSendDocumentToSigningEnabled}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isSendDocumentToSigningEnabled ? lang.disable : lang.enable }}
            />
            &nbsp;
            {form.data.isSendDocumentToSigningEnabled && (
              <>
                <SelectInput
                  name="documentSigningType"
                  label={lang.documentSigningType}
                  data={[ContractSigningTypes.DocuSign, ContractSigningTypes.SignFlow].map((it) => ({
                    id: it,
                    title: lang[it],
                  }))}
                  value={form.data.documentSigningType}
                  error={form.errors.documentSigningType}
                  onChange={form.update}
                  icon={<Icon class="fas-file-signature" />}
                />
                &nbsp;
                <CheckInput
                  name="sendSigningEmailCarbonCopy"
                  label={lang.sendSigningEmailCarbonCopy}
                  class={inputStyles.check.switch}
                  style={{ margin: `${Theme.Size.S} 0` }}
                  value={form.data.sendSigningEmailCarbonCopy}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data.sendSigningEmailCarbonCopy ? lang.disable : lang.enable }}
                />
              </>
            )}
          </div>
        </div>
        <div style={sectionStyle}>
          <div style={switchGroupStyle}>
            <h4>{lang.consent}</h4>
            <CheckInput
              name="isCustomerConsentRequired"
              label={lang.isCustomerConsentRequired}
              class={inputStyles.check.switch}
              style={{ margin: `${Theme.Size.S} 0` }}
              value={form.data.isCustomerConsentRequired}
              onChange={form.update}
              htmlElementProps={{ title: form.data.isCustomerConsentRequired ? lang.disable : lang.enable }}
            />
            &nbsp;
          </div>
          &nbsp;
          {form.data?.isCustomerConsentRequired && (
            <InputWrapper>
              <TextInput
                name="consentSenderEmail"
                label={lang.consentSenderEmail}
                value={form.data.consentSenderEmail}
                error={form.errors.consentSenderEmail}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              &nbsp;
              <TextInput
                name="consentVideoLink"
                label={lang.consentVideoLink}
                value={form.data.consentVideoLink}
                error={form.errors.consentVideoLink}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
            </InputWrapper>
          )}
          &nbsp;
        </div>
      </div>
      <NumberInput
        label={lang.legacyId}
        name="legacyId"
        value={form.data.legacyId ? Number(form.data.legacyId) : null}
        error={form.errors.legacyId}
        onChange={form.update}
        icon={<Icon class="fas-link" />}
      />
      <SelectInput
        name="insurers"
        label={lang.insurers}
        data={insurersPayload?.data?.map((it) => it.displayInfo)}
        value={form.data.insurers?.map((it) => it.id)}
        error={form.errors.insurers?._objectError}
        onChange={(selectData) =>
          form.update({
            insurers: selectData.insurers
              .map((it) => insurersPayload?.data?.find((d) => d.id === it))
              ?.filter((it) => (it ? true : false)),
          })
        }
        isMultiselect
        icon={<Icon class="fas-users" />}
      />
      <SelectInput
        name="subfee"
        label={lang.surcharges}
        data={subfeesPayload?.data?.map((it) => it.displayInfo)}
        value={form.data.subfee?.map((it) => it.id)}
        error={form.errors.subfee?._objectError}
        onChange={(selectData) =>
          form.update({
            subfee: selectData.subfee
              .map((it) => subfeesPayload?.data?.find((d) => d.id === it))
              ?.filter((it) => (it ? true : false)),
          })
        }
        isMultiselect
        icon={<Icon class="fas-users" />}
      />
      <SelectInput
        name="pricingDistributionTemplateId"
        label={lang.pricingDistributionTemplate}
        data={distributionsPayload?.data?.map((it) => it.displayInfo)}
        value={form.data.pricingDistributionTemplateId}
        error={form.errors.pricingDistributionTemplateId}
        onChange={form.update}
        disabled={!!form.data.id}
        icon={<Icon class="fas-balance-scale" />}
      />
    </FormWrapper>
  );
}
